import React from "react";
import { Link } from "react-router-dom";

import { AUTOPILOT_COLORS } from "../autopilot/styles";
import { fmtBRL, fmtDataHora, fmtDataPura } from "../../views/Empresa/ConferenciaFatura/formatters";

// PO1-3367: a fatura do CT-e na Auditoria de CT-e (lista, resumo do detalhe e
// aba "Fatura"). O backend devolve `fatura` (lista: por linha; detalhe: com
// decisão e histórico da conferência) e o motivo do bloqueio de recálculo.

export const URL_AUDITORIA_FATURAS = "/app/flow/operacao/auditoria-faturas";

export function urlFatura(fatura) {
  return `${URL_AUDITORIA_FATURAS}?idFatura=${encodeURIComponent(fatura.id)}`;
}

const ROTULO_VINCULO = { 1: "A validar", 2: "OK", 3: "Não OK" };

export function rotuloVinculo(vinculo) {
  return ROTULO_VINCULO[Number(vinculo)] || "-";
}

const linkStyle = {
  color: AUTOPILOT_COLORS.accent,
  fontWeight: 700,
  textDecoration: "underline",
};

// PO1-3478: com `onAbrir`, o clique abre o detalhe da fatura em modal, sem
// sair da Auditoria de CT-e. Ctrl/Cmd/Shift/botão do meio continuam abrindo a
// Auditoria de Faturas em outra aba pelo href. Sem `onAbrir`, navega como antes.
export function LinkFatura({ fatura, onAbrir, pararPropagacao = false }) {
  const handleClick = (e) => {
    if (pararPropagacao) e.stopPropagation();
    if (!onAbrir) return;
    if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    onAbrir(fatura);
  };
  return (
    <Link to={urlFatura(fatura)} style={linkStyle} onClick={handleClick} title="Ver detalhe da fatura">
      {fatura.numero}
    </Link>
  );
}

// Célula da lista: "699907" (link) e a situação embaixo.
export function FaturaDoCteCelula({ fatura, onAbrir }) {
  if (!fatura) return <span style={{ color: AUTOPILOT_COLORS.muted }}>-</span>;
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", lineHeight: 1.25 }}>
      <LinkFatura fatura={fatura} onAbrir={onAbrir} pararPropagacao />
      <span style={{ fontSize: "0.72rem", color: AUTOPILOT_COLORS.muted }}>
        {fatura.situacaoNome || fatura.situacaoCodigo}
      </span>
    </span>
  );
}

// Valor do campo "Fatura" no resumo do detalhe: "699907 · Paga · CT-e OK".
export function FaturaDoCteResumo({ fatura, onAbrir }) {
  if (!fatura) return <span>Sem fatura</span>;
  return (
    <span>
      <LinkFatura fatura={fatura} onAbrir={onAbrir} />
      {` · ${fatura.situacaoNome || fatura.situacaoCodigo} · CT-e ${rotuloVinculo(fatura.vinculo)}`}
    </span>
  );
}

const campoLabelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  fontWeight: 600,
};

const campoValorStyle = { color: AUTOPILOT_COLORS.text, fontSize: "0.9rem", marginTop: "0.15rem" };

function Campo({ label, children }) {
  return (
    <div>
      <div style={campoLabelStyle}>{label}</div>
      <div style={campoValorStyle}>{children}</div>
    </div>
  );
}

const blocoStyle = {
  border: `1px solid ${AUTOPILOT_COLORS.border}`,
  borderRadius: 10,
  padding: "0.85rem 1rem",
  background: AUTOPILOT_COLORS.insetSurface,
};

// Conteúdo da aba "Fatura" do detalhe do CT-e.
export function FaturaDoCteAba({ fatura, onAbrir }) {
  if (!fatura) {
    return (
      <div style={{ ...blocoStyle, color: AUTOPILOT_COLORS.muted }}>
        Este CT-e ainda não está em nenhuma fatura.
      </div>
    );
  }
  const { decisao, historico = [] } = fatura;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
      <div
        style={{
          ...blocoStyle,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: "0.85rem",
        }}
      >
        <Campo label="Fatura">
          <LinkFatura fatura={fatura} onAbrir={onAbrir} />
        </Campo>
        <Campo label="Situação">{fatura.situacaoNome || fatura.situacaoCodigo}</Campo>
        <Campo label="Este CT-e na conferência">{rotuloVinculo(fatura.vinculo)}</Campo>
        <Campo label="Responsável">{fatura.responsavel || "-"}</Campo>
        <Campo label="Emissão">{fmtDataPura(fatura.emissao) || "-"}</Campo>
        <Campo label="Vencimento">{fmtDataPura(fatura.vencimento) || "-"}</Campo>
        <Campo label="Total da fatura">{fatura.totalFatura === null ? "-" : fmtBRL(fatura.totalFatura)}</Campo>
        <Campo label="CT-e (OK / Não OK)">
          {fatura.qtdCtes === null ? "-" : `${fatura.qtdCtes} (${fatura.qtdOk || 0} / ${fatura.qtdNok || 0})`}
        </Campo>
      </div>

      {fatura.aceitaDivergencia ? (
        <div
          role="note"
          style={{
            ...blocoStyle,
            borderColor: AUTOPILOT_COLORS.statusSuccessBorder,
            background: AUTOPILOT_COLORS.statusSuccessBg,
          }}
        >
          Diferença deste CT-e aceita na conferência da fatura (marcado OK).
        </div>
      ) : null}

      {fatura.bloqueioRecalculo ? (
        <div role="note" style={{ ...blocoStyle, color: AUTOPILOT_COLORS.muted }}>
          {fatura.bloqueioRecalculo}
        </div>
      ) : null}

      <div style={blocoStyle}>
        <div style={{ ...campoLabelStyle, marginBottom: "0.4rem" }}>Decisão</div>
        {decisao ? (
          <div>
            <div style={{ color: AUTOPILOT_COLORS.text, whiteSpace: "pre-line", lineHeight: 1.5 }}>{decisao.descricao}</div>
            <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem", marginTop: "0.35rem" }}>
              {[decisao.usuario, fmtDataHora(decisao.data)].filter(Boolean).join(" · ")}
            </div>
          </div>
        ) : (
          <div style={{ color: AUTOPILOT_COLORS.muted }}>Nenhuma decisão manual registrada nesta fatura.</div>
        )}
      </div>

      {historico.length ? (
        <details style={blocoStyle}>
          <summary style={{ cursor: "pointer", color: AUTOPILOT_COLORS.text, fontWeight: 600 }}>
            Histórico da conferência ({historico.length})
          </summary>
          <ul style={{ listStyle: "none", padding: 0, margin: "0.6rem 0 0", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {historico.map((h) => (
              <li key={h.id} style={{ borderTop: `1px solid ${AUTOPILOT_COLORS.borderFaint}`, paddingTop: "0.5rem" }}>
                <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.75rem" }}>
                  {[fmtDataHora(h.data), h.usuario].filter(Boolean).join(" · ")}
                </div>
                <div style={{ color: AUTOPILOT_COLORS.text, whiteSpace: "pre-line", lineHeight: 1.45, fontSize: "0.85rem" }}>
                  {h.descricao}
                </div>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
